import { useRmaStore } from '@/entities/rma';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useLayoutEffect, useMemo } from 'react';
import { t } from 'ttag';
import type { MarkDeviceReturnedModalProps } from './markDeviceReturnedModal.types';

/**
 * Drives the mark-device-returned modal's received-items checklist, return date and saving flag.
 * Builds a pre-checked checklist from the RMA store's patientReturnConfig ordered by position,
 * resets checklist, saving flag and return date (taken from the device only when exactly one is
 * selected) each time the modal opens, and wraps onConfirm with the saving flag.
 *
 * @param props - Modal props carrying isOpen, the selected device rows in data and the onConfirm
 * handler.
 * @returns The modal state, the confirm, checkbox and date handlers, and the device wording for
 * the title.
 */
const useMarkDeviceReturnedModal = ({
  isOpen,
  data,
  onConfirm: onConfirmProp,
}: Pick<MarkDeviceReturnedModalProps, 'isOpen' | 'data' | 'onConfirm'>) => {
  /**
   * The patient device-return configuration read from the RMA Zustand store. Selects patientReturnConfig, whose receivedItems drive the modal's checklist.
   *
   * @returns The patient return configuration.
   */
  const patientReturnConfig = useRmaStore((s) => s.patientReturnConfig);

  const [state, setState] = useSetState<any>({
    receivedItems: [],
    isSaving: false,
  });

  /**
   * The initial received-item checklist for the mark-device-returned modal, with every item pre-checked. Derived from the RMA store's patientReturnConfig by ordering its receivedItems on position and keeping only the name plus a checkBox flag.
   *
   * @returns The pre-checked received-item checklist.
   */
  const defaultReceivedItems = useMemo(() => {
    const { receivedItems } = patientReturnConfig;
    return _.map(_.orderBy(receivedItems, 'position'), (item) => ({
      name: item.name,
      checkBox: true,
    }));
  }, [patientReturnConfig]);

  /**
   * Confirms the mark-device-returned modal by handing the checklist and return date to the caller. Raises isSaving, awaits the onConfirm prop with state.receivedItems and state.returnDate, then lowers the flag again.
   *
   * @returns Resolves once the caller's confirm handler has settled.
   */
  const onConfirm = async () => {
    setState({ isSaving: true });
    await onConfirmProp(state.receivedItems, state.returnDate);
    setState({ isSaving: false });
  };

  /**
   * Toggles one entry of the received-items checklist in the mark-device-returned modal. Clones the checklist, sets checkBox on the item whose name matches and commits the clone, leaving state untouched when no item matches.
   *
   * @param name - Name of the received item being toggled.
   * @param value - New checked state for that item.
   * @returns Nothing.
   */
  const onChangeCheckBox = (name: any, value: any) => {
    const cloneReceivedItems = [...state.receivedItems];
    /**
     * The received-item entry whose name matches the toggled checkbox. Located by a linear search over the cloned received-item list.
     *
     * @returns The matching received item, or undefined.
     */
    const findedItem = cloneReceivedItems.find((item) => item.name === name);
    if (findedItem) {
      findedItem.checkBox = value;
      setState({ receivedItems: cloneReceivedItems });
    }
  };

  /**
   * Records the device return date picked in the mark-device-returned modal. Merges the date into state as returnDate.
   *
   * @param date - Return date selected in the picker.
   * @returns Nothing.
   */
  const onChangeDate = (date: any) => {
    setState({ returnDate: date });
  };

  useLayoutEffect(() => {
    // reset state
    if (isOpen) {
      setState({
        receivedItems: _.cloneDeep(defaultReceivedItems),
        isSaving: false,
        returnDate: data.length === 1 ? data[0]?.returnDate : undefined,
      });
    }
  }, [isOpen]);

  const deviceText = data?.length === 1 ? 'device' : `${data?.length} ${t`devices`}`;

  return {
    state,
    onConfirm,
    onChangeCheckBox,
    onChangeDate,
    deviceText,
  };
};

export { useMarkDeviceReturnedModal };
